/**
 * Global circuit breaker for Anthropic API quota / billing failures.
 *
 * Quota errors are account-wide and time-bound: once the API reports the spend limit
 * is reached, EVERY subsequent call fails identically until the limit resets. Without
 * this guard each meeting rediscovers that independently, and each rediscovery costs
 * a full retry ladder (5 retries x 7 recovery attempts = 35 calls per meeting), reset
 * on every page reload. The breaker turns that into a single call.
 */

import { logger } from '@/utils/logger';

const STORAGE_KEY = 'ai-quota-block';
const DEFAULT_BLOCK_MS = 60 * 60 * 1000; // 1 hour when no reset time is given

export type AIErrorKind = 'quota' | 'auth' | 'invalid' | 'retryable';

interface QuotaBlock {
  until: number;
  reason: string;
}

/**
 * Classifies an error message to decide whether retrying can possibly help.
 * Only 'retryable' should ever be retried — the rest fail identically every time.
 */
export function classifyAIError(message: string | undefined | null): AIErrorKind {
  const msg = (message || '').toLowerCase();

  if (
    msg.includes('usage limit') ||
    msg.includes('spend limit') ||
    msg.includes('credit balance') ||
    msg.includes('quota') ||
    msg.includes('billing') ||
    msg.includes('rate_limit_error') ||
    msg.includes('insufficient_quota')
  ) {
    return 'quota';
  }

  if (msg.includes('unauthorized') || msg.includes('expired') || msg.includes('not authenticated')) {
    return 'auth';
  }

  // A malformed request fails identically no matter how often it is repeated.
  if (msg.includes('invalid_request_error') || msg.includes('missing required field')) {
    return 'invalid';
  }

  return 'retryable';
}

/**
 * Anthropic states the reset time in the quota message, e.g.
 * "You will regain access on 2026-10-01 at 00:00 UTC."
 */
function parseResetTime(message: string): number | null {
  const match = message.match(/regain access on (\d{4}-\d{2}-\d{2})(?: at (\d{2}:\d{2}))?/i);
  if (!match) return null;

  const parsed = Date.parse(`${match[1]}T${match[2] || '00:00'}:00Z`);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Opens the breaker. Call this the first time a quota error is seen. */
export function recordQuotaError(message: string): void {
  const until = parseResetTime(message) ?? Date.now() + DEFAULT_BLOCK_MS;
  const block: QuotaBlock = { until, reason: message.slice(0, 300) };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(block));
  } catch {
    // Private browsing / blocked storage — the breaker still holds for this page.
  }

  logger.error(
    `[AI-QuotaGuard] Anthropic quota exhausted. Blocking all AI generation until ${new Date(until).toISOString()}. Reason: ${block.reason}`,
  );
}

/** True while the Anthropic quota is known to be exhausted. */
export function isQuotaBlocked(): boolean {
  return getQuotaBlock() !== null;
}

export function getQuotaBlock(): QuotaBlock | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const block = JSON.parse(raw) as QuotaBlock;
    if (Date.now() >= block.until) {
      clearQuotaBlock();
      return null;
    }
    return block;
  } catch {
    clearQuotaBlock();
    return null;
  }
}

export function clearQuotaBlock(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
